// Starlyzed /findmy — sections/starlyzed-findmy.liquid.
//
// Two tiers, one page, no reload:
//   1. No email: Sun, Moon and Rising from OUR engine through the existing `big-three` data hop
//      (Rising only with an exact birth time; a Moon that changed sign on an untimed birthday is shown
//      as both signs).
//   2. "Email me my full chart": the full chart from the `findmy` data hop (every planet + Chiron +
//      North Node by sign and degree, houses and Ascendant/MC only with an exact time) plus the chart
//      wheel from the existing `wheel` render, revealed on the page at once; the same hop carries the
//      email so the server saves the contact and emails the chart. Contracts:
//      .team/store-polish/findmy/CONTRACT.md and EMAIL-CONTRACT.md (2026-10-21).
//   Houses come back in two systems in the one response (`house` in `houseSystem`, plus
//   `wholeSignHouse`), so the Whole Sign / Placidus switch above the list flips with no refetch.
//   Contract: .team/findmy-houses/plan.md (2026-10-31).
//
// PRIVACY CONTRACT. Birth date, time, place and coordinates live in this element's memory for the
// life of the page. They go to the chart service through the same-origin app proxy to be computed and
// nowhere else: no storage, no cookie, no URL, no analytics, no hidden input. No input on the page has a
// `name`, so even a no-JS submit cannot put anything in a URL. The email rides the same findmy hop with
// exactly three companions — the separate, unchecked marketing opt-in as a strict boolean, the UTM
// source slug and the house system on screen — and nothing else: no tags, no copy, no HTML (the server
// derives tags from the chart it computes and owns every word of the email).
// scripts/tests/theme-findmy.test.mjs holds it.
//
// Every DOM-free function is exported so node can prove the contract; the custom element at the
// bottom is the only code that touches `document`.

import { requestChartPreview } from './chart-preview-client.js';

export const SIGNS_TEMPLATE = 'big-three';
export const FINDMY_TEMPLATE = 'findmy';
export const WHEEL_TEMPLATE = 'wheel';
export const DEFAULT_PROXY_BASE = '/apps/chart-preview';
export const DEFAULT_TIMEOUT_MS = 10000;
export const CAPABILITY_HEADER = 'X-Preview-Capability';
const PHOTON_ENDPOINT = 'https://photon.komoot.io/api/';
const PLACE_FETCH_LIMIT = 30;
const PLACE_MAX_SUGGESTIONS = 6;
const PLAIN_PLACE_TYPES = new Set(['city', 'town', 'village', 'hamlet']);
const POPULATED_PLACE_TYPES = new Set(['city', 'town', 'village', 'hamlet', 'suburb', 'quarter', 'neighbourhood', 'borough', 'locality']);
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const UTM_SOURCE_RE = /^[a-z0-9][a-z0-9_-]{0,63}$/;
export const EMAIL_DELIVERY_STATUSES = Object.freeze(['sent', 'rate_limited', 'unavailable']);
const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^\d{2}:\d{2}$/;
const DEGREE_RE = /^(\d{1,2})°(\d{2})'?$/;

export const SIGNS = Object.freeze([
  'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
  'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces',
]);
// U+FE0E asks for the text presentation, so iOS draws the glyph in ink instead of a purple emoji tile.
const TEXT_STYLE = '︎';
export const SIGN_GLYPHS = Object.freeze({
  Aries: '♈', Taurus: '♉', Gemini: '♊', Cancer: '♋', Leo: '♌', Virgo: '♍',
  Libra: '♎', Scorpio: '♏', Sagittarius: '♐', Capricorn: '♑', Aquarius: '♒', Pisces: '♓',
});

/** The twelve placements the `findmy` hop answers, in reading order, with the words a newcomer needs. */
export const PLACEMENTS = Object.freeze([
  { id: 'sun', label: 'Sun', meaning: 'Who you are' },
  { id: 'moon', label: 'Moon', meaning: 'What you need' },
  { id: 'mercury', label: 'Mercury', meaning: 'How you think' },
  { id: 'venus', label: 'Venus', meaning: 'How you love' },
  { id: 'mars', label: 'Mars', meaning: 'How you move' },
  { id: 'jupiter', label: 'Jupiter', meaning: 'Where you grow' },
  { id: 'saturn', label: 'Saturn', meaning: 'Where you’re tested' },
  { id: 'uranus', label: 'Uranus', meaning: 'Where you break the rules' },
  { id: 'neptune', label: 'Neptune', meaning: 'Where you dream' },
  { id: 'pluto', label: 'Pluto', meaning: 'Where you transform' },
  { id: 'chiron', label: 'Chiron', meaning: 'The wound that teaches' },
  { id: 'north_node', label: 'North Node', meaning: 'Where you’re headed' },
]);
/** The two house systems the switch offers, Whole Sign first (the default). */
export const HOUSE_SYSTEMS = Object.freeze(['whole_sign', 'placidus']);
export const DEFAULT_HOUSE_SYSTEM = 'whole_sign';
export const HOUSE_SYSTEM_NAMES = Object.freeze({ whole_sign: 'Whole Sign', placidus: 'Placidus' });
export const HOUSE_SYSTEM_LINE = 'Whole Sign gives each house one sign. Placidus divides the sky by time.';
export const ANGLES = Object.freeze([
  { id: 'asc', label: 'Ascendant', meaning: 'How you arrive' },
  { id: 'mc', label: 'Midheaven', meaning: 'Where you’re seen' },
]);

export class FindmyError extends Error {
  /** @param {'input'|'capability'|'engine'|'malformed'|'network'|'timeout'} kind */
  constructor(kind, message, code = '') {
    super(message);
    this.name = 'FindmyError';
    this.kind = kind;
    this.code = code;
  }
}

function isSign(value) {
  return typeof value === 'string' && SIGNS.includes(value);
}

export function signGlyph(sign) {
  return SIGN_GLYPHS[sign] ? `${SIGN_GLYPHS[sign]}${TEXT_STYLE}` : '';
}

// --- input ----------------------------------------------------------------------------------------

/**
 * Validate the form state into the chart both data hops accept. An unselected place has no
 * coordinates and is refused here, before any network call.
 *
 * @returns {{ ok: true, chart: { date: string, time: string, timeKnown: boolean, lat: number, lon: number } } | { ok: false, error: FindmyError, field: string }}
 */
export function normalizeBirthInput({ date, time, timeUnknown, lat, lon } = {}) {
  const dateText = String(date || '').trim();
  const timeText = String(time || '').trim();
  const latText = String(lat ?? '').trim();
  const lonText = String(lon ?? '').trim();
  const latNumber = Number(latText);
  const lonNumber = Number(lonText);
  const refuse = (field, message) => ({ ok: false, field, error: new FindmyError('input', message) });

  if (!DATE_RE.test(dateText)) return refuse('date', 'Add your birthday.');
  const timeKnown = timeUnknown !== true && timeText !== '';
  if (!timeKnown && timeUnknown !== true) return refuse('time', 'Add your birth time, or tap “I don’t know my birth time”.');
  if (timeKnown && !TIME_RE.test(timeText)) return refuse('time', 'Use a clock time like 09:40.');
  if (!latText || !lonText || !Number.isFinite(latNumber) || !Number.isFinite(lonNumber)
    || Math.abs(latNumber) > 90 || Math.abs(lonNumber) > 180) {
    return refuse('place', 'Choose your birthplace from the list.');
  }
  return {
    ok: true,
    chart: { date: dateText, time: timeKnown ? timeText : '', timeKnown, lat: latNumber, lon: lonNumber },
  };
}

// --- engine calls (capability mint, then one data hop, both same-origin) ---------------------------

function sameOriginBase(base) {
  const raw = String(base || DEFAULT_PROXY_BASE).trim() || DEFAULT_PROXY_BASE;
  const unavailable = () => new FindmyError('input', 'Your chart is unavailable right now.');
  if (typeof location === 'undefined' || !location.origin) {
    if (/^(?:[a-z][a-z\d+.-]*:|\/\/)/i.test(raw)) throw unavailable();
    return raw.replace(/\/+$/, '') || DEFAULT_PROXY_BASE;
  }
  let url;
  try {
    url = new URL(raw, location.origin);
  } catch {
    throw unavailable();
  }
  if (url.origin !== location.origin) throw unavailable();
  return url.pathname.replace(/\/+$/, '') || DEFAULT_PROXY_BASE;
}

async function fetchWithTimeout(fetchImpl, url, init, { timeoutMs, setTimeoutImpl, clearTimeoutImpl, AbortControllerImpl }) {
  const controller = typeof AbortControllerImpl === 'function' ? new AbortControllerImpl() : null;
  let timer = null;
  let timedOut = false;
  const request = Promise.resolve().then(() => fetchImpl(url, controller ? { ...init, signal: controller.signal } : init));
  const timeout = new Promise((_, reject) => {
    timer = setTimeoutImpl(() => {
      timedOut = true;
      try {
        controller?.abort();
      } catch {
        // a non-standard controller must not prevent the bounded rejection
      }
      reject(new FindmyError('timeout', 'That took too long. Please try again.'));
    }, Number.isFinite(timeoutMs) && timeoutMs > 0 ? timeoutMs : DEFAULT_TIMEOUT_MS);
  });
  try {
    return await Promise.race([request, timeout]);
  } catch (error) {
    if (timedOut || error?.kind === 'timeout') throw new FindmyError('timeout', 'That took too long. Please try again.');
    throw error;
  } finally {
    if (timer !== null) clearTimeoutImpl(timer);
  }
}

async function readJson(response) {
  try {
    return { parsed: true, value: await response.json() };
  } catch {
    return { parsed: false, value: null };
  }
}

/** Engine and Worker codes, said in a way a visitor can act on. Anything else keeps the fallback. */
export function engineErrorMessage(code, fallback) {
  if (code === 'ambiguous_civil_time') {
    return 'That time happened twice on your birthday (the clocks went back). Try an hour earlier or later, or tap “I don’t know my birth time”.';
  }
  if (code === 'nonexistent_civil_time') {
    return 'That time didn’t exist where you were born (the clocks skipped it). Check it, or tap “I don’t know my birth time”.';
  }
  if (code === 'template_not_permitted') return 'This page isn’t switched on yet. Please check back soon.';
  if (code === 'rate_limited') return 'A lot of people are reading their sky right now. Try again in a minute.';
  return fallback;
}

/** One capability mint for `template`, spent on one POST to `<base>/<template>`. Returns the parsed body. */
async function spendCapability({ base, template, chart, extra = null, fetchImpl, timing }) {
  const resolvedBase = sameOriginBase(base);
  const unreachable = () => new FindmyError('network', 'Your chart is unavailable right now. Please try again.');

  let capabilityResponse;
  try {
    capabilityResponse = await fetchWithTimeout(
      fetchImpl,
      `${resolvedBase}/capability?template=${encodeURIComponent(template)}`,
      { method: 'POST', headers: { Accept: 'application/json' }, body: '' },
      timing,
    );
  } catch (error) {
    if (error?.kind === 'timeout') throw error;
    throw unreachable();
  }
  const capabilityResult = await readJson(capabilityResponse);
  const capabilityBody = capabilityResult.value;
  if (!capabilityResponse.ok) {
    const code = capabilityBody?.error || '';
    const message = code === 'template_not_permitted' && template === FINDMY_TEMPLATE
      ? 'Full charts aren’t switched on yet. Please check back soon.'
      : engineErrorMessage(code, 'We couldn’t start your chart. Please try again.');
    throw new FindmyError('capability', message, code);
  }
  const capability = typeof capabilityBody?.capability === 'string' ? capabilityBody.capability.trim() : '';
  if (!capabilityResult.parsed || !capability || (capabilityBody.template && capabilityBody.template !== template)) {
    throw new FindmyError('malformed', 'We couldn’t start your chart. Please try again.');
  }

  let response;
  try {
    response = await fetchWithTimeout(
      fetchImpl,
      `${resolvedBase}/${template}`,
      {
        method: 'POST',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', [CAPABILITY_HEADER]: capability },
        body: JSON.stringify({
          template,
          date: chart.date,
          time: chart.timeKnown ? chart.time : '',
          timeKnown: chart.timeKnown === true,
          lat: chart.lat,
          lon: chart.lon,
          ...(extra || {}),
        }),
      },
      timing,
    );
  } catch (error) {
    if (error?.kind === 'timeout') throw error;
    throw unreachable();
  }
  const result = await readJson(response);
  const body = result.value;
  if (!response.ok) {
    throw new FindmyError('engine', engineErrorMessage(body?.error, body?.message || 'We couldn’t read that chart. Please try again.'), body?.error || '');
  }
  if (!result.parsed || !body || typeof body !== 'object') {
    throw new FindmyError('malformed', 'Your chart came back incomplete. Please try again.');
  }
  return body;
}

function timingOptions({ timeoutMs, setTimeoutImpl, clearTimeoutImpl, AbortControllerImpl }) {
  return { timeoutMs, setTimeoutImpl, clearTimeoutImpl, AbortControllerImpl };
}

function assertChart(chart) {
  if (!chart || !DATE_RE.test(String(chart.date || '')) || !Number.isFinite(chart.lat) || !Number.isFinite(chart.lon)) {
    throw new FindmyError('input', 'Add your birth details first.');
  }
}

/**
 * Tier 1 — Sun, Moon, Rising from the existing `big-three` hop.
 * @returns {Promise<{ sun: string, moon: string, moonCandidates: string[], rising: string|null, timeKnown: boolean, chartMathVersion: string|null }>}
 */
export async function requestSigns({
  base = DEFAULT_PROXY_BASE,
  chart,
  fetchImpl = fetch,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  setTimeoutImpl = setTimeout,
  clearTimeoutImpl = clearTimeout,
  AbortControllerImpl = typeof AbortController === 'function' ? AbortController : null,
}) {
  assertChart(chart);
  const body = await spendCapability({
    base, template: SIGNS_TEMPLATE, chart, fetchImpl,
    timing: timingOptions({ timeoutMs, setTimeoutImpl, clearTimeoutImpl, AbortControllerImpl }),
  });
  if (!isSign(body.sun) || !isSign(body.moon)) throw new FindmyError('malformed', 'Your chart came back incomplete. Please try again.');
  const timeKnown = body.timeKnown === true;
  return {
    sun: body.sun,
    moon: body.moon,
    moonCandidates: Array.isArray(body.moonCandidates) ? body.moonCandidates.filter(isSign) : [],
    // Never show a Rising the engine did not compute from a known time.
    rising: timeKnown && isSign(body.rising) ? body.rising : null,
    timeKnown,
    chartMathVersion: typeof body.chartMathVersion === 'string' ? body.chartMathVersion : null,
  };
}

function isHouse(value) {
  return Number.isInteger(value) && value >= 1 && value <= 12;
}

function cleanDegree(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  return DEGREE_RE.test(text) ? text : null;
}

/**
 * Validate a `findmy` body against the contract and keep only the fields the page shows. Anything
 * missing or out of shape fails the whole chart — a placement is never invented or guessed.
 *
 * Houses: `house` is in `houseSystem` ('placidus', or 'whole_sign' on the engine's polar fallback;
 * null when the Worker predates the field, so the page cannot name it). `wholeSignHouse` rides with
 * every timed placement or none (an older Worker); a partial or out-of-range set fails the chart.
 * `houseSystems` lists what the switch offers: both only when a Placidus chart also carries Whole Sign.
 */
export function normalizeFullChart(body) {
  const malformed = () => new FindmyError('malformed', 'Your full chart came back incomplete. Please try again.');
  if (!body || typeof body !== 'object' || !Array.isArray(body.placements)) throw malformed();
  const timeKnown = body.timeKnown === true;
  const byId = new Map();
  for (const item of body.placements) {
    if (item && typeof item.id === 'string') byId.set(item.id, item);
  }
  const wholeSignCount = timeKnown
    ? PLACEMENTS.filter(({ id }) => byId.get(id)?.wholeSignHouse !== undefined).length
    : 0;
  if (wholeSignCount !== 0 && wholeSignCount !== PLACEMENTS.length) throw malformed();
  const hasWholeSign = wholeSignCount === PLACEMENTS.length;
  const placements = PLACEMENTS.map(({ id, label, meaning }) => {
    const raw = byId.get(id);
    const degree = cleanDegree(raw?.degree);
    if (!raw || !isSign(raw.sign) || !degree || typeof raw.retrograde !== 'boolean') throw malformed();
    let house = null;
    let wholeSignHouse = null;
    if (timeKnown) {
      if (!isHouse(raw.house)) throw malformed();
      house = raw.house;
      if (hasWholeSign) {
        if (!isHouse(raw.wholeSignHouse)) throw malformed();
        wholeSignHouse = raw.wholeSignHouse;
      }
    }
    return {
      id,
      label,
      meaning,
      sign: raw.sign,
      degree,
      retrograde: raw.retrograde,
      house,
      wholeSignHouse,
      uncertain: !timeKnown && (raw.degree_uncertain === true || raw.sign_uncertain === true),
    };
  });
  const houseSystem = timeKnown && HOUSE_SYSTEMS.includes(body.houseSystem) ? body.houseSystem : null;
  const houseSystems = houseSystem === 'placidus' && hasWholeSign ? [...HOUSE_SYSTEMS] : [];
  let angles = null;
  if (timeKnown) {
    angles = ANGLES.map(({ id, label, meaning }) => {
      const raw = body.angles?.[id];
      const degree = cleanDegree(raw?.degree);
      if (!raw || !isSign(raw.sign) || !degree) throw malformed();
      return { id, label, meaning, sign: raw.sign, degree };
    });
  }
  return {
    placements,
    angles,
    timeKnown,
    houseSystem,
    houseSystems,
    moonCandidates: Array.isArray(body.moonCandidates) ? body.moonCandidates.filter(isSign) : [],
    chartMathVersion: typeof body.chartMathVersion === 'string' ? body.chartMathVersion : null,
  };
}

/**
 * The house system the page shows for `full`, given what the visitor picked. With the switch, the
 * pick (Whole Sign unless they chose Placidus); on the polar fallback, Whole Sign; on an older Worker,
 * whatever `houseSystem` said (null: unnamed); untimed, null.
 */
export function resolveHouseSystem(full, wanted = DEFAULT_HOUSE_SYSTEM) {
  if (full?.timeKnown !== true) return null;
  if (Array.isArray(full.houseSystems) && full.houseSystems.length > 1) {
    return full.houseSystems.includes(wanted) ? wanted : DEFAULT_HOUSE_SYSTEM;
  }
  return HOUSE_SYSTEMS.includes(full.houseSystem) ? full.houseSystem : null;
}

/** A placement's house number in `system`, or null when the chart does not carry that system. */
export function houseIn(placement, full, system) {
  if (full?.timeKnown !== true || !placement) return null;
  if (system === full.houseSystem || system == null) return isHouse(placement.house) ? placement.house : null;
  if (system === 'whole_sign' && isHouse(placement.wholeSignHouse)) return placement.wholeSignHouse;
  return null;
}

/**
 * The email companions for the findmy hop, or null when there is no email to send. `marketingOptIn` is
 * true only when the visitor ticked the box themselves; the UTM source rides only when it is a clean slug;
 * `emailHouseSystem` is the system on screen, Whole Sign unless the visitor picked Placidus.
 */
export function emailFields({ email, marketingOptIn, utmSource, houseSystem } = {}) {
  if (!isEmail(email)) return null;
  const fields = { email: email.trim(), marketingOptIn: marketingOptIn === true };
  if (typeof utmSource === 'string' && UTM_SOURCE_RE.test(utmSource)) fields.utmSource = utmSource;
  fields.emailHouseSystem = HOUSE_SYSTEMS.includes(houseSystem) ? houseSystem : DEFAULT_HOUSE_SYSTEM;
  return fields;
}

/** What the server said about the chart email. A missing or unknown answer is never read as sent. */
export function readEmailDelivery(body) {
  const status = body?.emailDelivery?.status;
  return EMAIL_DELIVERY_STATUSES.includes(status) ? status : 'unavailable';
}

/**
 * Tier 2 — the full chart from the `findmy` hop, and (with `email`) the chart email. Returns the chart
 * plus `emailDelivery` ('sent' | 'rate_limited' | 'unavailable', or null when no email was given).
 */
export async function requestFullChart({
  base = DEFAULT_PROXY_BASE,
  chart,
  email = null,
  fetchImpl = fetch,
  timeoutMs = DEFAULT_TIMEOUT_MS,
  setTimeoutImpl = setTimeout,
  clearTimeoutImpl = clearTimeout,
  AbortControllerImpl = typeof AbortController === 'function' ? AbortController : null,
}) {
  assertChart(chart);
  const extra = email ? emailFields(email) : null;
  if (email && !extra) throw new FindmyError('input', 'Check your email address.');
  const body = await spendCapability({
    base, template: FINDMY_TEMPLATE, chart, extra, fetchImpl,
    timing: timingOptions({ timeoutMs, setTimeoutImpl, clearTimeoutImpl, AbortControllerImpl }),
  });
  return { ...normalizeFullChart(body), emailDelivery: extra ? readEmailDelivery(body) : null };
}

/** The wheel needs an exact time; without one we do not fabricate a time or request a timed wheel. */
export async function requestWheel({ base = DEFAULT_PROXY_BASE, chart, requestImpl = requestChartPreview, ...rest }) {
  if (!chart || chart.timeKnown !== true) return null;
  const result = await requestImpl({
    base,
    template: WHEEL_TEMPLATE,
    chart: { date: chart.date, time: chart.time, lat: chart.lat, lon: chart.lon },
    ...rest,
  });
  return typeof result?.svg === 'string' ? result.svg : null;
}

// --- honest presentation ---------------------------------------------------------------------------

/** "13°38'" → "13°38′"; without a birth time the minute is noise, so it reads "about 13°". */
export function formatDegree(degree, { approximate = false } = {}) {
  const match = DEGREE_RE.exec(String(degree || '').trim());
  if (!match) return '';
  return approximate ? `about ${Number(match[1])}°` : `${Number(match[1])}°${match[2]}′`;
}

/** "10th house", or with the system named: "10th Whole Sign house", "9th Placidus house". */
export function ordinalHouse(house, system = null) {
  if (!isHouse(house)) return '';
  const suffix = house === 1 ? 'st' : house === 2 ? 'nd' : house === 3 ? 'rd' : 'th';
  const name = HOUSE_SYSTEM_NAMES[system];
  return `${house}${suffix} ${name ? `${name} ` : ''}house`;
}

/** The three reveals. Rising without a time is stated as missing, never guessed; a split Moon shows both. */
export function describeSigns(result) {
  const moonCandidates = Array.isArray(result?.moonCandidates) ? result.moonCandidates.filter(isSign) : [];
  const moonSplit = result?.timeKnown !== true && moonCandidates.length > 1;
  const rising = result?.timeKnown === true && isSign(result.rising) ? result.rising : null;
  return [
    { key: 'sun', label: 'Sun', sign: result.sun, glyph: signGlyph(result.sun), line: 'Who you are at your core.', note: '' },
    {
      key: 'moon',
      label: 'Moon',
      sign: moonSplit ? moonCandidates.join(' or ') : result.moon,
      glyph: moonSplit ? moonCandidates.map(signGlyph).join(' ') : signGlyph(result.moon),
      line: 'What you need to feel at home.',
      split: moonSplit,
      note: moonSplit ? 'The Moon changed signs on your birthday. Your birth time settles which.' : '',
    },
    rising
      ? { key: 'rising', label: 'Rising', sign: rising, glyph: signGlyph(rising), line: 'How you arrive in a room.', note: '' }
      : { key: 'rising', label: 'Rising', sign: null, glyph: '', line: '', note: 'Your Rising needs your birth time. We won’t guess it.' },
  ];
}

/**
 * Rows for the full chart, in reading order, with nothing the engine did not give us. `house` is the
 * label in the system on screen; `houses` holds the label in every system the switch offers, so a flip
 * only changes which one shows.
 */
export function describeFullChart(full, { houseSystem: wanted = DEFAULT_HOUSE_SYSTEM } = {}) {
  const approximate = full?.timeKnown !== true;
  const moonCandidates = Array.isArray(full?.moonCandidates) ? full.moonCandidates.filter(isSign) : [];
  const houseSystem = resolveHouseSystem(full, wanted);
  const houseSystems = approximate || !Array.isArray(full.houseSystems) ? [] : full.houseSystems;
  const rows = full.placements.map((p) => {
    const moonSplit = approximate && p.id === 'moon' && moonCandidates.length > 1;
    const houses = Object.fromEntries(houseSystems.map((system) => [system, ordinalHouse(houseIn(p, full, system), system)]));
    return {
      id: p.id,
      label: p.label,
      meaning: p.meaning,
      sign: moonSplit ? moonCandidates.join(' or ') : p.sign,
      glyph: moonSplit ? '' : signGlyph(p.sign),
      degree: moonSplit ? '' : formatDegree(p.degree, { approximate }),
      house: approximate ? '' : ordinalHouse(houseIn(p, full, houseSystem), houseSystem),
      houses,
      // The Node is retrograde by nature; flagging it reads as noise to a beginner. Rx is for planets.
      retrograde: p.id !== 'north_node' && p.retrograde === true,
    };
  });
  const angles = (full.angles || []).map((a) => ({
    id: a.id,
    label: a.label,
    meaning: a.meaning,
    sign: a.sign,
    glyph: signGlyph(a.sign),
    degree: formatDegree(a.degree),
    house: '',
    houses: {},
    retrograde: false,
  }));
  return { rows, angles, timeKnown: !approximate, houseSystem, houseSystems: [...houseSystems] };
}

// --- the chart email (consent SSOT = Shopify emailMarketingConsent, set by the server) ----------------

export function isEmail(value) {
  const text = typeof value === 'string' ? value.trim() : '';
  return EMAIL_RE.test(text) && text.length <= 254;
}

/** The redirector's utm_source, reduced to the contract's slug, or ''. Nothing else is read off the URL. */
export function readUtmSource(search) {
  const value = new URLSearchParams(String(search || '')).get('utm_source') || '';
  const slug = value.toLowerCase().replace(/[^a-z0-9_]+/g, '-').replace(/^[-_]+|-+$/g, '').slice(0, 64);
  return UTM_SOURCE_RE.test(slug) ? slug : '';
}

/** The quiet line under the full chart that says what happened to the email, honestly. */
export function emailDeliveryLine(status) {
  if (status === 'sent') return 'Your chart is on its way to your inbox.';
  if (status === 'rate_limited') return 'We’ve already emailed a chart to this address today. Everything is here on the page.';
  if (status === 'unavailable') return 'We couldn’t email your chart just now. Everything is here on the page.';
  return '';
}


// --- place search (Photon, same disambiguation as the quiz and the Big Three module) ----------------

export function placeLabel(feature, { county = false } = {}) {
  const props = feature?.properties || {};
  const name = props.name || props.city || '';
  const type = String(props.osm_value || '').replace(/_/g, ' ');
  const typed = name && type && !PLAIN_PLACE_TYPES.has(type) ? `${name} (${type})` : name;
  return [typed, county ? props.county : '', props.state, props.country].filter(Boolean).join(', ');
}

export function rankPlaces(features) {
  const list = Array.isArray(features) ? features : [];
  const isPopulated = (f) => POPULATED_PLACE_TYPES.has(String((f.properties || {}).osm_value || '').replace(/_/g, ' '));
  const ordered = [...list.filter(isPopulated), ...list.filter((f) => !isPopulated(f))];
  let places = ordered.map((feature) => ({ feature, label: placeLabel(feature) }));
  const counts = new Map();
  for (const item of places) counts.set(item.label, (counts.get(item.label) || 0) + 1);
  places = places.map((item) => (counts.get(item.label) > 1 ? { feature: item.feature, label: placeLabel(item.feature, { county: true }) } : item));
  const seen = new Set();
  return places.filter((item) => {
    if (!item.label || seen.has(item.label)) return false;
    seen.add(item.label);
    return true;
  }).slice(0, PLACE_MAX_SUGGESTIONS).map((item) => {
    const coords = item.feature?.geometry?.coordinates || [];
    return { label: item.label, lon: coords[0] != null ? String(coords[0]) : '', lat: coords[1] != null ? String(coords[1]) : '' };
  });
}

/**
 * The engine's screen wheel is light ink on a black canvas (engine/service/profiles.py `screen`). On the
 * page it should be drawn in the page's own ink with no box: drop the full-canvas background and map its
 * two inks to currentColor. Geometry is untouched. An SVG in any other palette is returned as it came.
 */
export function retintWheel(svgText) {
  if (typeof svgText !== 'string') return svgText;
  const background = /<rect\s+x="0"\s+y="0"\s+width="[\d.]+"\s+height="[\d.]+"\s+fill="#0{3}(?:0{3})?"\s*\/>/i;
  if (!background.test(svgText)) return svgText;
  return svgText
    .replace(background, '')
    .replace(/\b(fill|stroke)="#(?:EDEDED|CFCFCF)"/gi, '$1="currentColor"');
}

/** Strip anything executable from the engine's wheel SVG before it enters the page. */
export function sanitizeSvg(svgText, DOMParserImpl = typeof DOMParser === 'function' ? DOMParser : null) {
  if (!DOMParserImpl || typeof svgText !== 'string') return null;
  const doc = new DOMParserImpl().parseFromString(svgText, 'image/svg+xml');
  const svg = doc.documentElement;
  if (!svg || svg.nodeName.toLowerCase() !== 'svg' || doc.getElementsByTagName('parsererror').length) return null;
  for (const node of [...svg.querySelectorAll('script, foreignObject, iframe, object, embed')]) node.remove();
  for (const node of [svg, ...svg.querySelectorAll('*')]) {
    for (const attr of [...node.attributes]) {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();
      if (name.startsWith('on') || ((name === 'href' || name === 'xlink:href') && value.startsWith('javascript:'))) node.removeAttribute(attr.name);
    }
  }
  if (!svg.getAttribute('viewBox')) {
    const w = parseFloat(svg.getAttribute('width'));
    const h = parseFloat(svg.getAttribute('height'));
    if (w > 0 && h > 0) svg.setAttribute('viewBox', `0 0 ${w} ${h}`);
  }
  svg.removeAttribute('width');
  svg.removeAttribute('height');
  svg.setAttribute('role', 'img');
  return svg;
}

function debounce(fn, wait) {
  let timer = null;
  return (...args) => {
    if (timer) clearTimeout(timer);
    timer = setTimeout(() => fn(...args), wait);
  };
}

// --- the element -----------------------------------------------------------------------------------

if (typeof customElements !== 'undefined' && !customElements.get('starlyzed-findmy')) {
  const reduceMotion = () => typeof matchMedia === 'function' && matchMedia('(prefers-reduced-motion: reduce)').matches;

  const el = (tag, className, text) => {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  };

  class StarlyzedFindmy extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      // In-memory only. Nothing in this object is ever written to storage, the URL or the DOM as data.
      this.state = { place: null, chart: null, signs: null, full: null, busy: false, unlocked: false, houseSystem: DEFAULT_HOUSE_SYSTEM };
      this.proxyBase = this.dataset.proxyBase || DEFAULT_PROXY_BASE;
      this.$ = (selector) => this.querySelector(selector);

      const date = this.$('[data-fm-date]');
      if (date && !date.max) date.max = new Date().toISOString().slice(0, 10);

      const unknown = this.$('[data-fm-time-unknown]');
      unknown?.addEventListener('change', () => {
        const time = this.$('[data-fm-time]');
        if (!time) return;
        time.disabled = unknown.checked;
        if (unknown.checked) time.value = '';
        this.closest('.stz-fm')?.classList.toggle('is-untimed', unknown.checked);
      });

      const place = this.$('[data-fm-place]');
      const list = this.$('[data-fm-suggestions]');
      if (place) {
        place.addEventListener('input', () => {
          this.state.place = null;
        });
        place.addEventListener('input', debounce(() => this.searchPlace(place, list), 250));
        place.addEventListener('keydown', (event) => this.navigateSuggestions(event, list));
        place.addEventListener('blur', () => setTimeout(() => this.closeSuggestions(list), 150));
      }

      this.$('[data-fm-ask]')?.addEventListener('submit', (event) => {
        event.preventDefault();
        this.reveal();
      });
      this.gate = this.$('[data-fm-gate-form]');
      if (this.gate) {
        this.gate.addEventListener('submit', (event) => {
          event.preventDefault();
          this.unlock();
        });
      }
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-fm-edit]')) this.edit();
        if (event.target.closest('[data-fm-add-time]')) this.edit({ addTime: true });
        const system = event.target.closest('[data-fm-house-system]');
        if (system) this.setHouseSystem(system.dataset.fmHouseSystem);
      });
    }

    setStatus(selector, message, { error = false } = {}) {
      const node = this.$(selector);
      if (!node) return;
      node.textContent = message || '';
      node.hidden = !message;
      node.classList.toggle('is-error', Boolean(error));
    }

    // --- place picker ---

    closeSuggestions(list) {
      if (!list) return;
      list.hidden = true;
      list.replaceChildren();
      this.$('[data-fm-place]')?.setAttribute('aria-expanded', 'false');
    }

    choosePlace(input, list, item) {
      input.value = item.label;
      this.state.place = { label: item.label, lat: item.lat, lon: item.lon };
      this.closeSuggestions(list);
    }

    navigateSuggestions(event, list) {
      if (!list || list.hidden) return;
      const options = [...list.querySelectorAll('button')];
      if (!options.length) return;
      const index = options.indexOf(document.activeElement);
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        options[Math.min(index + 1, options.length - 1)].focus();
      } else if (event.key === 'Escape') {
        this.closeSuggestions(list);
      }
    }

    async searchPlace(input, list) {
      const query = input.value.trim();
      if (!list || query.length < 3) {
        this.closeSuggestions(list);
        return;
      }
      try {
        const response = await fetch(`${PHOTON_ENDPOINT}?q=${encodeURIComponent(query)}&limit=${PLACE_FETCH_LIMIT}&lang=en&osm_tag=place`);
        if (!response.ok || input.value.trim() !== query) return;
        const data = await response.json();
        const places = rankPlaces(data.features);
        list.replaceChildren(...places.map((item) => {
          const li = el('li');
          const button = el('button', null, item.label);
          button.type = 'button';
          button.addEventListener('mousedown', (event) => {
            event.preventDefault();
            this.choosePlace(input, list, item);
          });
          button.addEventListener('click', () => {
            this.choosePlace(input, list, item);
            input.focus();
          });
          li.append(button);
          return li;
        }));
        list.hidden = places.length === 0;
        input.setAttribute('aria-expanded', String(places.length > 0));
      } catch {
        this.closeSuggestions(list);
      }
    }

    // --- tier 1 ---

    async reveal() {
      if (this.state.busy) return;
      const normalized = normalizeBirthInput({
        date: this.$('[data-fm-date]')?.value,
        time: this.$('[data-fm-time]')?.value,
        timeUnknown: Boolean(this.$('[data-fm-time-unknown]')?.checked),
        lat: this.state.place?.lat,
        lon: this.state.place?.lon,
      });
      if (!normalized.ok) {
        this.setStatus('[data-fm-ask-status]', normalized.error.message, { error: true });
        this.$(`[data-fm-${normalized.field}]`)?.focus();
        return;
      }
      this.state.busy = true;
      const button = this.$('[data-fm-reveal-button]');
      const label = button?.textContent;
      if (button) {
        button.disabled = true;
        button.textContent = this.dataset.busyLabel || 'Reading your sky…';
      }
      this.setStatus('[data-fm-ask-status]', '');
      try {
        const signs = await requestSigns({ base: this.proxyBase, chart: normalized.chart });
        this.state.chart = normalized.chart;
        this.state.signs = signs;
        this.state.full = null;
        this.renderSigns(signs);
      } catch (error) {
        this.setStatus('[data-fm-ask-status]', error?.message || 'Something went wrong. Please try again.', { error: true });
      } finally {
        this.state.busy = false;
        if (button) {
          button.disabled = false;
          button.textContent = label;
        }
      }
      // Someone who already opened their full chart and changed a detail gets it again without re-asking.
      if (this.state.signs && this.state.unlocked) this.unlock({ resend: false });
    }

    renderSigns(signs) {
      const root = this.closest('.stz-fm') || this;
      const panel = this.$('[data-fm-reveal]');
      if (!panel) return;
      const parts = describeSigns(signs);
      const list = el('div', 'stz-fm__three');
      parts.forEach((part, index) => {
        const item = el('div', `stz-fm__sign stz-fm__sign--${part.key}`);
        item.style.setProperty('--i', String(index));
        item.append(el('p', 'stz-fm__sign-label', part.label));
        if (part.sign) {
          const name = el('p', 'stz-fm__sign-name');
          if (part.glyph) name.append(el('span', 'stz-fm__glyph', part.glyph));
          name.append(el('span', null, part.sign));
          item.append(name);
          if (part.line) item.append(el('p', 'stz-fm__sign-line', part.line));
        } else {
          item.classList.add('is-missing');
        }
        if (part.split) item.classList.add('stz-fm__sign--split');
        if (part.note) item.append(el('p', 'stz-fm__sign-note', part.note));
        if (!part.sign) {
          const add = el('button', 'stz-fm__quiet', this.dataset.addTimeLabel || 'Add my birth time');
          add.type = 'button';
          add.setAttribute('data-fm-add-time', '');
          item.append(add);
        }
        list.append(item);
      });
      const edit = el('button', 'stz-fm__quiet', this.dataset.editLabel || 'Change details');
      edit.type = 'button';
      edit.setAttribute('data-fm-edit', '');
      panel.replaceChildren(list, edit);

      this.$('[data-fm-ask]').hidden = true;
      this.$('[data-fm-full]')?.replaceChildren();
      this.$('[data-fm-full]')?.setAttribute('hidden', '');
      const gate = this.$('[data-fm-gate]');
      if (gate) gate.hidden = false;
      const gateBody = this.$('[data-fm-gate-body]');
      if (gateBody) {
        const text = signs.timeKnown ? gateBody.dataset.timed : gateBody.dataset.untimed;
        gateBody.textContent = text || '';
        gateBody.hidden = !text;
      }
      this.setStatus('[data-fm-gate-status]', '');
      panel.hidden = false;
      root.classList.add('is-revealed');
      this.turnRing(signs);
      requestAnimationFrame(() => panel.classList.add('is-in'));
      if (typeof panel.scrollIntoView === 'function') {
        const top = panel.getBoundingClientRect().top;
        if (top > innerHeight * 0.6 || top < 0) panel.scrollIntoView({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
      }
      panel.focus?.({ preventScroll: true });
    }

    /** The decorative ring stops drifting and turns the Sun's sign to the top. */
    turnRing(signs) {
      const ring = this.$('[data-fm-ring]');
      if (!ring) return;
      ring.querySelectorAll('.is-lit').forEach((node) => node.classList.remove('is-lit'));
      for (const sign of [signs.sun, signs.moonCandidates?.length > 1 && !signs.timeKnown ? null : signs.moon, signs.rising]) {
        if (sign) ring.querySelector(`[data-fm-glyph="${sign}"]`)?.classList.add('is-lit');
      }
      const target = -(SIGNS.indexOf(signs.sun) * 30 + 15);
      if (reduceMotion()) {
        ring.style.animation = 'none';
        ring.style.transform = `rotate(${target}deg)`;
        return;
      }
      const matrix = getComputedStyle(ring).transform;
      let current = 0;
      const m = /matrix\(([^,]+),\s*([^,]+)/.exec(matrix || '');
      if (m) current = Math.atan2(Number(m[2]), Number(m[1])) * (180 / Math.PI);
      let delta = ((target - current) % 360 + 360) % 360; // always turn forward with the drift
      if (delta < 40) delta += 360;
      ring.style.animation = 'none';
      ring.style.transition = 'none';
      ring.style.transform = `rotate(${current}deg)`;
      void ring.getBoundingClientRect();
      ring.style.transition = 'transform 1.8s cubic-bezier(0.16, 0.84, 0.24, 1)';
      ring.style.transform = `rotate(${current + delta}deg)`;
    }

    edit({ addTime = false } = {}) {
      const root = this.closest('.stz-fm') || this;
      this.$('[data-fm-reveal]')?.setAttribute('hidden', '');
      this.$('[data-fm-reveal]')?.classList.remove('is-in');
      this.$('[data-fm-gate]')?.setAttribute('hidden', '');
      this.$('[data-fm-full]')?.setAttribute('hidden', '');
      this.$('[data-fm-full]')?.replaceChildren();
      this.state.signs = null;
      this.state.full = null;
      this.state.chart = null;
      root.classList.remove('is-revealed', 'is-full');
      const ring = this.$('[data-fm-ring]');
      if (ring) {
        ring.querySelectorAll('.is-lit').forEach((node) => node.classList.remove('is-lit'));
        ring.style.transition = '';
        ring.style.transform = '';
        ring.style.animation = '';
      }
      const ask = this.$('[data-fm-ask]');
      if (ask) ask.hidden = false;
      const unknown = this.$('[data-fm-time-unknown]');
      if (addTime && unknown?.checked) {
        unknown.checked = false;
        unknown.dispatchEvent(new Event('change'));
      }
      this.$(addTime ? '[data-fm-time]' : '[data-fm-date]')?.focus();
    }

    // --- tier 2 ---

    /**
     * Reveal the full chart on the page and, with the email, ask the server to send it. `resend: false`
     * is the quiet re-open after "Change details": the chart comes back, no second email is asked for.
     */
    async unlock({ resend = true } = {}) {
      if (this.state.busy || !this.state.chart || !this.state.signs) return;
      const emailInput = this.$('[data-fm-email]');
      const email = emailInput?.value || '';
      if (resend && !isEmail(email)) {
        this.setStatus('[data-fm-gate-status]', 'Check your email address.', { error: true });
        emailInput?.focus();
        return;
      }
      const request = resend
        ? {
          email,
          marketingOptIn: this.$('[data-fm-optin]')?.checked === true,
          utmSource: readUtmSource(typeof location !== 'undefined' ? location.search : ''),
          houseSystem: this.state.houseSystem,
        }
        : null;
      this.state.busy = true;
      const button = this.$('[data-fm-unlock-button]');
      const label = button?.textContent;
      if (button) {
        button.disabled = true;
        button.textContent = this.dataset.unlockingLabel || 'Opening your chart…';
      }
      this.setStatus('[data-fm-gate-status]', '');

      const chart = this.state.chart;
      try {
        const [full, wheel] = await Promise.all([
          requestFullChart({ base: this.proxyBase, chart, email: request }),
          requestWheel({ base: this.proxyBase, chart }).catch(() => 'failed'),
        ]);
        if (this.state.chart !== chart) return; // the visitor changed their details meanwhile
        this.state.full = full;
        this.state.unlocked = true;
        this.renderFull(full, wheel);
      } catch (error) {
        this.setStatus('[data-fm-gate-status]', error?.message || 'Something went wrong. Please try again.', { error: true });
      } finally {
        this.state.busy = false;
        if (button) {
          button.disabled = false;
          button.textContent = label;
        }
      }
    }

    renderFull(full, wheelSvg) {
      const root = this.closest('.stz-fm') || this;
      const panel = this.$('[data-fm-full]');
      if (!panel) return;
      const copy = describeFullChart(full, { houseSystem: this.state.houseSystem });
      const nodes = [el('h2', 'stz-fm__full-title', this.dataset.fullHeading || 'Your whole sky')];
      const delivery = emailDeliveryLine(full.emailDelivery);
      if (delivery) {
        const line = el('p', `stz-fm__delivery${full.emailDelivery === 'sent' ? '' : ' is-quiet'}`, delivery);
        line.setAttribute('role', 'status');
        nodes.push(line);
      }

      if (copy.timeKnown) {
        const figure = el('figure', 'stz-fm__wheel');
        const svg = typeof wheelSvg === 'string' ? sanitizeSvg(retintWheel(wheelSvg)) : null;
        if (svg) {
          svg.setAttribute('aria-label', 'Your birth chart wheel');
          figure.append(document.importNode(svg, true));
          nodes.push(figure);
        } else {
          nodes.push(el('p', 'stz-fm__aside', 'Your wheel didn’t load this time. Your placements are all here.'));
        }
      }

      // Two systems: a quiet switch right above the list, and every row carries both labels in one
      // grid cell (the hidden one keeps its width), so a flip changes no row's size.
      const switchable = copy.houseSystems.length > 1;
      if (switchable) {
        const group = el('div', 'stz-fm__systems');
        group.setAttribute('role', 'group');
        group.setAttribute('aria-label', 'House system');
        for (const system of copy.houseSystems) {
          const button = el('button', 'stz-fm__system', HOUSE_SYSTEM_NAMES[system]);
          button.type = 'button';
          button.dataset.fmHouseSystem = system;
          button.setAttribute('aria-pressed', String(system === copy.houseSystem));
          group.append(button);
        }
        // One sentence per span, so a narrow screen breaks the line between sentences, not inside one.
        const why = el('p', 'stz-fm__systems-line');
        HOUSE_SYSTEM_LINE.split(/(?<=\.) /).forEach((sentence, i) => why.append(i ? ' ' : '', el('span', null, sentence)));
        nodes.push(group, why);
      }

      const list = el('ol', `stz-fm__list${switchable ? ' stz-fm__list--systems' : ''}`);
      list.setAttribute('data-fm-list', '');
      if (switchable) list.dataset.houseSystem = copy.houseSystem;
      const metaText = (degree, house) => [degree, house].filter(Boolean).join(' · ');
      [...copy.rows, ...copy.angles].forEach((row, index) => {
        const item = el('li', `stz-fm__row${row.id === 'asc' ? ' stz-fm__row--angles' : ''}`);
        item.style.setProperty('--i', String(index));
        const left = el('div', 'stz-fm__row-who');
        left.append(el('p', 'stz-fm__row-name', row.label), el('p', 'stz-fm__row-meaning', row.meaning));
        const right = el('div', 'stz-fm__row-where');
        const sign = el('p', 'stz-fm__row-sign');
        if (row.glyph) sign.append(el('span', 'stz-fm__glyph', row.glyph));
        sign.append(el('span', null, row.sign));
        right.append(sign);
        const rx = () => {
          const abbr = el('abbr', 'stz-fm__rx', 'Rx');
          abbr.title = 'Retrograde';
          return abbr;
        };
        const systems = Object.keys(row.houses || {});
        if (switchable && systems.length) {
          const meta = el('div', 'stz-fm__row-meta stz-fm__row-meta--systems');
          for (const system of systems) {
            const detail = metaText(row.degree, row.houses[system]);
            const line = el('p', 'stz-fm__row-house', detail);
            line.dataset.system = system;
            if (row.retrograde) line.append(detail ? ' · ' : '', rx());
            meta.append(line);
          }
          right.append(meta);
        } else {
          const detail = metaText(row.degree, row.house);
          const meta = el('p', 'stz-fm__row-meta', detail);
          if (row.retrograde) meta.append(detail ? ' · ' : '', rx());
          if (detail || row.retrograde) right.append(meta);
        }
        item.append(left, right);
        list.append(item);
      });
      nodes.push(list);

      if (!copy.timeKnown) {
        nodes.push(el('p', 'stz-fm__aside', 'Degrees are approximate without a birth time. Houses, your Ascendant and the wheel need the exact time.'));
      }

      const bridgeUrl = this.dataset.bridgeUrl;
      if (bridgeUrl) {
        const bridge = el('a', 'stz-fm__bridge', this.dataset.bridgeLabel || 'Wear your chart');
        bridge.href = bridgeUrl;
        nodes.push(bridge);
      }

      panel.replaceChildren(...nodes);
      this.$('[data-fm-gate]')?.setAttribute('hidden', '');
      panel.hidden = false;
      root.classList.add('is-full');
      requestAnimationFrame(() => panel.classList.add('is-in'));
      panel.scrollIntoView?.({ behavior: reduceMotion() ? 'auto' : 'smooth', block: 'start' });
    }

    /** Flip the houses in place: no refetch, no re-render, focus stays on the button. */
    setHouseSystem(system) {
      const full = this.state.full;
      if (!full || !HOUSE_SYSTEMS.includes(system) || !full.houseSystems?.includes(system)) return;
      this.state.houseSystem = system;
      const list = this.$('[data-fm-list]');
      if (list) list.dataset.houseSystem = system;
      this.querySelectorAll('[data-fm-house-system]').forEach((button) => {
        button.setAttribute('aria-pressed', String(button.dataset.fmHouseSystem === system));
      });
    }
  }

  customElements.define('starlyzed-findmy', StarlyzedFindmy);
}
